/*!
 * Copyright 2023 - Swiss Data Science Center (SDSC)
 * A partnership between École Polytechnique Fédérale de Lausanne (EPFL) and
 * Eidgenössische Technische Hochschule Zürich (ETHZ).
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { clamp } from "lodash-es";

import type { HomepageParams } from "../../features/landing/anonymousHome.types";
import {
  APP_LOBBY_CONFIG_BOUNDS,
  type AppLobbyConfig,
} from "../../features/sessionsV2/apps/appLobby.utils";
import { DEFAULT_APP_PARAMS } from "./appParams.constants";
import type {
  AppParams,
  AppParamsBooleans,
  AppParamsNumbers,
  AppParamsStrings,
  CullingThresholdsParams,
  PreviewThresholdParams,
  PrivacyBannerLayoutParams,
  SessionClassEmailUsParams,
  TemplatesParams,
  UploadThresholdParams,
} from "./appParams.types";

export function validatedAppParams(params: unknown): AppParams {
  if (typeof params !== "object" || params == null) {
    return DEFAULT_APP_PARAMS;
  }

  const params_ = params as RawAppParams;

  // String params
  const BASE_URL = validateString(params_, "BASE_URL");
  const GATEWAY_URL = validateString(params_, "GATEWAY_URL");
  const KEYCLOAK_REALM = validateString(params_, "KEYCLOAK_REALM");
  const MAINTENANCE = validateString(params_, "MAINTENANCE");
  const PRIVACY_BANNER_CONTENT = validateString(
    params_,
    "PRIVACY_BANNER_CONTENT",
  );
  const RENKU_CHART_VERSION = validateString(params_, "RENKU_CHART_VERSION");
  const SENTRY_NAMESPACE = validateString(params_, "SENTRY_NAMESPACE");
  const SENTRY_SAMPLE_RATE = validateString(params_, "SENTRY_SAMPLE_RATE");
  const SENTRY_URL = validateString(params_, "SENTRY_URL");
  const STATUSPAGE_ID = validateString(params_, "STATUSPAGE_ID");
  const UISERVER_URL = validateString(params_, "UISERVER_URL");
  const UI_SHORT_SHA = validateString(params_, "UI_SHORT_SHA");
  const UI_VERSION = validateString(params_, "UI_VERSION");
  const CONTACT_EMAIL = validateString(params_, "CONTACT_EMAIL");

  // Boolean params
  const ANONYMOUS_SESSIONS = validateBoolean(params_, "ANONYMOUS_SESSIONS");
  const PRIVACY_BANNER_ENABLED = validateBoolean(
    params_,
    "PRIVACY_BANNER_ENABLED",
  );
  const TERMS_PAGES_ENABLED = validateBoolean(params_, "TERMS_PAGES_ENABLED");
  const IMAGE_BUILDERS_ENABLED = validateBoolean(
    params_,
    "IMAGE_BUILDERS_ENABLED",
  );
  const BUILD_PRIVATE_REPO_BUILDS_ENABLED = validateBoolean(
    params_,
    "BUILD_PRIVATE_REPO_BUILDS_ENABLED",
  );
  const PERSISTED_LOGS_ENABLED = validateBoolean(
    params_,
    "PERSISTED_LOGS_ENABLED",
  );
  const APPS_ENABLED = validateBoolean(params_, "APPS_ENABLED");

  // Integer params
  const USER_PREFERENCES_MAX_PINNED_PROJECTS = validateInteger(
    params_,
    "USER_PREFERENCES_MAX_PINNED_PROJECTS",
  );
  const PERSISTED_LOGS_TTL_SECONDS = validateInteger(
    params_,
    "PERSISTED_LOGS_TTL_SECONDS",
  );

  // Object params
  const HOMEPAGE = validateHomepage(params_);
  const PREVIEW_THRESHOLD = validatePreviewThreshold(params_);
  const PRIVACY_BANNER_LAYOUT = validatePrivacyBannerLayout(params_);
  const TEMPLATES = validateTemplates(params_);
  const UPLOAD_THRESHOLD = validateUploadThreshold(params_);
  const SESSION_CLASS_EMAIL_US = validateSessionClassEmailUs(params_);
  const CULLING_THRESHOLDS = validateCullingThresholds(params_);
  const APP_LOBBY = validateAppLobby(params_);

  return {
    ANONYMOUS_SESSIONS,
    BASE_URL,
    CONTACT_EMAIL,
    CULLING_THRESHOLDS,
    GATEWAY_URL,
    HOMEPAGE,
    IMAGE_BUILDERS_ENABLED,
    BUILD_PRIVATE_REPO_BUILDS_ENABLED,
    KEYCLOAK_REALM,
    MAINTENANCE,
    PREVIEW_THRESHOLD,
    PRIVACY_BANNER_CONTENT,
    PRIVACY_BANNER_ENABLED,
    PRIVACY_BANNER_LAYOUT,
    RENKU_CHART_VERSION,
    SENTRY_NAMESPACE,
    SENTRY_SAMPLE_RATE,
    SENTRY_URL,
    SESSION_CLASS_EMAIL_US,
    STATUSPAGE_ID,
    TEMPLATES,
    TERMS_PAGES_ENABLED,
    UI_SHORT_SHA,
    UI_VERSION,
    UISERVER_URL,
    UPLOAD_THRESHOLD,
    USER_PREFERENCES_MAX_PINNED_PROJECTS,
    PERSISTED_LOGS_ENABLED,
    PERSISTED_LOGS_TTL_SECONDS,
    APPS_ENABLED,
    APP_LOBBY,
  };
}

interface RawAppParams {
  [key: string]: unknown;
}

function validateString(
  params: RawAppParams,
  key: keyof AppParamsStrings,
): string {
  const value = params[key];
  if (typeof value !== "string") {
    return DEFAULT_APP_PARAMS[key];
  }
  return value.trim();
}

function validateBoolean(
  params: RawAppParams,
  key: keyof AppParamsBooleans,
): boolean {
  const value = params[key];

  // adjust boolean param value
  if (typeof value === "string") {
    if (value.trim().toLowerCase() === "true") {
      return true;
    }
    if (value.trim().toLowerCase() === "false") {
      return false;
    }
  }

  if (typeof value !== "boolean") {
    return DEFAULT_APP_PARAMS[key];
  }
  return value;
}

interface ValidateIntegerOptions {
  min?: number;
  max?: number;
}

function validateInteger(
  params: RawAppParams,
  key: keyof AppParamsNumbers,
  options?: ValidateIntegerOptions,
): number {
  return coerceInteger(params[key], DEFAULT_APP_PARAMS[key], options);
}

function coerceInteger(
  value: unknown,
  fallback: number,
  options?: ValidateIntegerOptions,
): number {
  const min = options?.min ?? Number.MIN_SAFE_INTEGER;
  const max = options?.max ?? Number.MAX_SAFE_INTEGER;

  // adjust integer param value
  if (typeof value === "string") {
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed)) {
      return clamp(parsed, min, max);
    }
  }

  if (typeof value !== "number" || !isFinite(value)) {
    return fallback;
  }
  return clamp(Math.trunc(value), min, max);
}

function validateAppLobby(params: RawAppParams): AppLobbyConfig {
  const defaults = DEFAULT_APP_PARAMS["APP_LOBBY"];

  const value = params["APP_LOBBY"];
  if (typeof value !== "object" || value == null) {
    return defaults;
  }

  const rawParams = value as {
    maxAttempts: unknown;
    probeTimeoutMs: unknown;
    retryDelayMs: unknown;
  };

  return {
    maxAttempts: coerceInteger(
      rawParams.maxAttempts,
      defaults.maxAttempts,
      APP_LOBBY_CONFIG_BOUNDS.maxAttempts,
    ),
    probeTimeoutMs: coerceInteger(
      rawParams.probeTimeoutMs,
      defaults.probeTimeoutMs,
      APP_LOBBY_CONFIG_BOUNDS.probeTimeoutMs,
    ),
    retryDelayMs: coerceInteger(
      rawParams.retryDelayMs,
      defaults.retryDelayMs,
      APP_LOBBY_CONFIG_BOUNDS.retryDelayMs,
    ),
  };
}

function validateHomepage(params: RawAppParams): HomepageParams {
  const value = params["HOMEPAGE"];
  if (typeof value !== "object" || value == null) {
    return DEFAULT_APP_PARAMS["HOMEPAGE"];
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return value as any;
}

function validatePreviewThreshold(
  params: RawAppParams,
): PreviewThresholdParams {
  const value = params["PREVIEW_THRESHOLD"];
  if (typeof value !== "object" || value == null) {
    return DEFAULT_APP_PARAMS["PREVIEW_THRESHOLD"];
  }

  const rawParams = value as { hard: unknown; soft: unknown };

  const hard =
    typeof rawParams.hard === "number"
      ? rawParams.hard
      : DEFAULT_APP_PARAMS["PREVIEW_THRESHOLD"].hard;
  const soft =
    typeof rawParams.soft === "number"
      ? rawParams.soft
      : DEFAULT_APP_PARAMS["PREVIEW_THRESHOLD"].soft;
  return { hard, soft };
}

function validatePrivacyBannerLayout(
  params: RawAppParams,
): PrivacyBannerLayoutParams | null {
  const value = params["PRIVACY_BANNER_LAYOUT"];
  if (typeof value !== "object" || value == null) {
    return DEFAULT_APP_PARAMS["PRIVACY_BANNER_LAYOUT"];
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return value as any;
}

function validateTemplates(params: RawAppParams): TemplatesParams {
  const value = params["TEMPLATES"];
  if (typeof value !== "object" || value == null) {
    return DEFAULT_APP_PARAMS["TEMPLATES"];
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return value as any;
}

function validateUploadThreshold(params: RawAppParams): UploadThresholdParams {
  const value = params["UPLOAD_THRESHOLD"];
  if (typeof value !== "object" || value == null) {
    return DEFAULT_APP_PARAMS["UPLOAD_THRESHOLD"];
  }

  const rawParams = value as { soft: unknown };

  const soft =
    typeof rawParams.soft === "number"
      ? rawParams.soft
      : DEFAULT_APP_PARAMS["PREVIEW_THRESHOLD"].soft;
  return { soft };
}

function validateCullingThresholds(
  params: RawAppParams,
): CullingThresholdsParams {
  const value = params["CULLING_THRESHOLDS"];
  if (typeof value !== "object" || value == null) {
    return DEFAULT_APP_PARAMS["CULLING_THRESHOLDS"];
  }

  const rawParams = value as {
    registered?: { idle: unknown; hibernation: unknown };
    anonymous?: { idle: unknown; hibernation: unknown };
  };

  const validateGroup = (
    group: { idle: unknown; hibernation: unknown } | undefined,
  ) => ({
    idle: typeof group?.idle === "number" ? group.idle : 0,
    hibernation: typeof group?.hibernation === "number" ? group.hibernation : 0,
  });

  return {
    registered: validateGroup(rawParams.registered),
    anonymous: validateGroup(rawParams.anonymous),
  };
}

function validateSessionClassEmailUs(
  params: RawAppParams,
): SessionClassEmailUsParams {
  const value = params["SESSION_CLASS_EMAIL_US"];
  if (typeof value !== "object" || value == null) {
    return DEFAULT_APP_PARAMS["SESSION_CLASS_EMAIL_US"];
  }

  const rawEmailUsParams = value as {
    [key: string]: unknown;
  };
  const rawEmailUsEmailParams =
    typeof rawEmailUsParams.email === "object" && rawEmailUsParams.email != null
      ? (rawEmailUsParams.email as {
          [key: string]: unknown;
        })
      : {};

  const enabled = !!rawEmailUsParams.enabled;

  const to =
    typeof rawEmailUsEmailParams.to === "string"
      ? rawEmailUsEmailParams.to
      : "";

  const subject =
    typeof rawEmailUsEmailParams.subject === "string"
      ? rawEmailUsEmailParams.subject
      : "";

  const body =
    typeof rawEmailUsEmailParams.body === "string"
      ? rawEmailUsEmailParams.body
      : "";

  if (enabled && to) {
    return {
      enabled,
      email: {
        to,
        subject,
        body,
      },
    };
  }

  return DEFAULT_APP_PARAMS["SESSION_CLASS_EMAIL_US"];
}
